import { useEffect, useState, useMemo } from 'react';
import Header from '../../components/Header/Header';
import TableContainer from '../../components/TableContainer/TableContainer';
import { ITechCheckItem, ITechList } from '../../types/type';
import StatusButtons from '../../components/StatusButtons/StatusButtons';
import TechList from '../../components/TechList/TechList';
import TechDetails from '../../components/TechDetails/TechDetails';
import { useBackendFilter } from '../../hooks/usePosts';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import './tech.css';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setTechLists } from '../../store/techdata/techdataSlice';
import Pagination from '../../components/Pagination/Pagination';
import AutoApproval from '../../components/AutoApproval/AutoApproval';
import { toBackendDate } from '../../utils/utils';
import { CHECK_STATUS_FINAL_MESSAGE, isCheckStatusFinal } from '../../utils/checkStatusErrors';
import { TechnicalService, NewTechListFilters } from '../../services/technical.service';

function Tech() {
  const user: any = useSelector((state: RootState) => state.user.user);
  const dispatch = useAppDispatch();
  const reduxTechLists = useAppSelector((state) => state.techdata.techLists);
  // const reduxTotalCount = useAppSelector((state) => state.techdata.totalCount);
  const reduxTotalPages = useAppSelector((state) => state.techdata.totalPages);

  // Добавляем состояние для отслеживания активной вкладки
  const [activeTab, setActiveTab] = useState<'closedTO' | 'auto' | 'newTO'>('newTO');

  const params = [
    {
      i_id: 'medlist',
      i_name: 'role',
      i_type: 'checkbox',
      i_value: 'lastName',
      i_label: 'Фамилия',
    },
    {
      i_id: 'medlist',
      i_name: 'role',
      i_type: 'checkbox',
      i_value: 'firstName',
      i_label: 'Имя',
    },
    {
      i_id: 'medlist',
      i_name: 'role',
      i_type: 'checkbox',
      i_value: 'firstName',
      i_label: 'Отчество',
    },
  ];

  const [status, setStatus] = useState('');
  const [filter, setFilter] = useState<{ sort: string; query: string; filterQuery: string }>({
    sort: '',
    query: '',
    filterQuery: 'fullName',
  });

  // Добавляем расширенные фильтры для closedTO
  const [extendedFilters, setExtendedFilters] = useState({
    startTime: '',
    endTime: '',
    autoparkName: '',
    num: '',
    carNumber: '',
  });

  // Добавляем состояния для newTO
  const [newTOData, setNewTOData] = useState<any[]>([]);
  const [newTOLoading, setNewTOLoading] = useState(false);
  const [newTOError, setNewTOError] = useState<string | null>(null);
  const [newTOPagination, setNewTOPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
  });

  // Фильтры для newTO
  const [newTOFilters, setNewTOFilters] = useState<NewTechListFilters>({
    page: 1,
    limit: 10,
    startTime: '',
    endTime: '',
    num: '',
    number: '',
    autoparkIDs: user?.autoparkIDs?.join(',') || '', // Дефолтное значение из user.autoparkIDs
    status: 'waiting', // Дефолтное значение для новых заявок
  });

  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Используем хук useBackendFilter для получения данных с сервера для closedTO
  const {
    data: techLists,
    loading,
    error,
    totalCount,
    totalPages,
    fetchData,
  } = useBackendFilter(`/techlist/technik/${user.id}`);

  // Статистика техосмотров для closedTO
  const techStats = useMemo(() => {
    const lists = (reduxTechLists as ITechCheckItem[]) || (techLists as ITechCheckItem[]) || [];
    const totalCount = lists.length || 0;
    const completeCount = lists.filter((tech) => tech.checkStatus === 'completed').length || 0;
    const uncompleteCount = lists.filter((tech) => tech.checkStatus === 'rejected').length || 0;
    const waitingCount = lists.filter((tech) => tech.checkStatus === 'waiting').length || 0;

    return {
      totalCount,
      completeCount,
      uncompleteCount,
      waitingCount,
    };
  }, [reduxTechLists, techLists]);

  // Статистика для newTO
  const newTOStats = useMemo(() => {
    const lists = newTOData || [];
    const totalCount = lists.length || 0;
    const completeCount = lists.filter((tech) => tech.checkStatus === 'completed').length || 0;
    const uncompleteCount = lists.filter((tech) => tech.checkStatus === 'rejected').length || 0;
    const waitingCount = lists.filter((tech) => tech.checkStatus === 'waiting').length || 0;

    return {
      totalCount,
      completeCount,
      uncompleteCount,
      waitingCount,
    };
  }, [newTOData]);

  // Обработчик изменения расширенных фильтров для closedTO
  const handleExtendedFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setExtendedFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Обработчик изменения фильтров для newTO
  const handleNewTOFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewTOFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Функция для сброса фильтров closedTO
  const resetFilters = () => {
    setFilter({
      sort: '',
      query: '',
      filterQuery: 'fullName',
    });
    setExtendedFilters({
      startTime: '',
      endTime: '',
      autoparkName: '',
      num: '',
      carNumber: '',
    });
    setStatus('');
    setCurrentPage(1);
  };

  // Функция для сброса фильтров newTO
  const resetNewTOFilters = () => {
    // Сбрасываем общие фильтры
    setFilter({
      sort: '',
      query: '',
      filterQuery: 'fullName',
    });
    setStatus('');

    // Сбрасываем специфичные фильтры newTO
    setNewTOFilters({
      page: 1,
      limit: 10,
      startTime: '',
      endTime: '',
      num: '',
      number: '',
      autoparkIDs: user?.autoparkIDs?.join(',') || '',
      status: 'waiting', // Дефолтное значение для новых заявок
    });
  };

  // Функция для получения данных newTO
  const fetchNewTOData = async (filterParams?: Record<string, any>) => {
    setNewTOLoading(true);
    setNewTOError(null);

    try {
      const params = filterParams || newTOFilters;
      const response = await TechnicalService.getTechList(params);
      setNewTOData(response.data);
      setNewTOPagination({
        currentPage: response.page,
        totalPages: response.totalPages,
        totalCount: response.totalCount,
      });
    } catch (err: any) {
      setNewTOError(err.message || 'Не удалось загрузить данные');
      setNewTOData([]);
    } finally {
      setNewTOLoading(false);
    }
  };

  // Функция для получения данных с сервера с применением фильтров для newTO
  const applyNewTOFilters = () => {
    const filterParams: Record<string, any> = {
      page: newTOFilters.page,
      limit: newTOFilters.limit,
    };

    // Добавляем autoparkIDs из newTOFilters или из пользователя
    if (newTOFilters.autoparkIDs || user?.autoparkIDs) {
      filterParams.autoparkIDs = newTOFilters.autoparkIDs || user.autoparkIDs.join(',');
    }

    // Добавляем статус из newTOFilters
    if (newTOFilters.status) {
      filterParams.status = newTOFilters.status;
    }

    // Добавляем поисковый запрос и поле поиска, если они есть
    if (filter.query && filter.filterQuery) {
      // Преобразование параметров поиска в соответствии с форматом бэкенда
      if (filter.filterQuery === 'firstName') {
        filterParams.firstName = filter.query;
      } else if (filter.filterQuery === 'lastName') {
        filterParams.lastName = filter.query;
      } else if (filter.filterQuery === 'fullName') {
        filterParams.fullName = filter.query;
      } else {
        // Для других параметров (gosNumber, date, mark, model) отправляем как есть
        filterParams[filter.filterQuery] = filter.query;
      }

      // Добавляем оригинальные параметры для совместимости
      filterParams.query = filter.query;
      filterParams.filterQuery = filter.filterQuery;
    }

    // Добавляем статус техосмотра из кнопок фильтра, если он выбран
    if (status) {
      filterParams.checkStatus = status;
    }

    // Добавляем сортировку, если она выбрана
    if (filter.sort) {
      filterParams.sort = filter.sort;
    }

    // Добавляем период дат из newTOFilters, если они указаны, конвертируя в нужный формат
    if (newTOFilters.startTime) {
      filterParams.startTime = toBackendDate(newTOFilters.startTime);
    }
    if (newTOFilters.endTime) {
      filterParams.endTime = toBackendDate(newTOFilters.endTime);
    }

    // Добавляем номер заявки из newTOFilters, если он указан
    if (newTOFilters.num) {
      filterParams.num = newTOFilters.num;
    }

    // Добавляем гос. номер из newTOFilters, если он указан
    if (newTOFilters.number) {
      filterParams.number = newTOFilters.number;
    }

    fetchNewTOData(filterParams);
  };

  // Функция для получения данных с сервера с применением фильтров для closedTO
  const applyFilters = () => {
    const filterParams: Record<string, any> = {
      page: currentPage,
      limit: itemsPerPage,
    };

    // Добавляем technicID если пользователь - техник
    if (user.id) {
      filterParams.technicID = user.id;
    }

    // Добавляем поисковый запрос и поле поиска, если они есть
    if (filter.query && filter.filterQuery) {
      // Преобразование параметров поиска в соответствии с форматом бэкенда
      if (filter.filterQuery === 'firstName') {
        // Если выбрано имя или отчество, отправляем в поле firstName
        filterParams.firstName = filter.query;
      } else if (filter.filterQuery === 'lastName') {
        // Если выбрана фамилия, отправляем в поле lastName
        filterParams.lastName = filter.query;
      } else if (filter.filterQuery === 'fullName') {
        // Если выбрано ФИО, отправляем оригинальный параметр
        filterParams.fullName = filter.query;
      } else {
        // Для других параметров (gosNumber, date, mark, model) отправляем как есть
        filterParams[filter.filterQuery] = filter.query;
      }

      // Добавляем оригинальные параметры для совместимости
      filterParams.query = filter.query;
      filterParams.filterQuery = filter.filterQuery;
    }

    // Добавляем статус техосмотра, если он выбран
    if (status) {
      filterParams.checkStatus = status;
    }

    // Добавляем сортировку, если она выбрана
    if (filter.sort) {
      filterParams.sort = filter.sort;
    }

    // Добавляем период дат, если они указаны, конвертируя в нужный формат
    if (extendedFilters.startTime) {
      filterParams.startTime = extendedFilters.startTime;
    }
    if (extendedFilters.endTime) {
      filterParams.endTime = extendedFilters.endTime;
    }

    // Добавляем автопарк, если он указан
    if (extendedFilters.autoparkName) {
      filterParams.autoparkName = extendedFilters.autoparkName;
    }

    // Добавляем номер заявки, если он указан
    if (extendedFilters.num) {
      filterParams.num = extendedFilters.num;
    }

    // Добавляем гос. номер, если он указан
    if (extendedFilters.carNumber) {
      filterParams.number = extendedFilters.carNumber;
    }
    console.log('filterParams: ', filterParams);

    fetchData(filterParams);
  };

  // Первоначальная загрузка данных для closedTO
  useEffect(() => {
    if (activeTab === 'closedTO') {
      applyFilters();
    }
  }, [user.id, activeTab]);

  // Первоначальная загрузка данных для newTO
  useEffect(() => {
    if (activeTab === 'newTO') {
      applyNewTOFilters();
    }
  }, [user.id, activeTab]);

  // Обновление данных при изменении фильтров или пагинации для closedTO
  useEffect(() => {
    if (activeTab === 'closedTO') {
      const handler = setTimeout(() => {
        applyFilters();
      }, 300);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [
    filter.query,
    filter.filterQuery,
    status,
    filter.sort,
    currentPage,
    itemsPerPage,
    extendedFilters.startTime,
    extendedFilters.endTime,
    extendedFilters.autoparkName,
    extendedFilters.num,
    extendedFilters.carNumber,
    activeTab,
  ]);

  // Обновление данных при изменении фильтров или пагинации для newTO
  useEffect(() => {
    if (activeTab === 'newTO') {
      const handler = setTimeout(() => {
        applyNewTOFilters();
      }, 300);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [
    filter.query,
    filter.filterQuery,
    status,
    filter.sort,
    newTOFilters.page,
    newTOFilters.limit,
    newTOFilters.startTime,
    newTOFilters.endTime,
    newTOFilters.num,
    newTOFilters.number,
    newTOFilters.autoparkIDs,
    newTOFilters.status,
    activeTab,
  ]);

  // Сохраняем данные в Redux при их получении для closedTO
  useEffect(() => {
    if (techLists && !loading && activeTab === 'closedTO') {
      dispatch(
        setTechLists({
          data: techLists,
          totalCount,
          totalPages,
        }),
      );
    }
  }, [techLists, loading, totalCount, totalPages, dispatch, activeTab]);

  const handleStatusChange = (newValue: string) => {
    if (newValue !== status) {
      setStatus(newValue);
      setCurrentPage(1); // Сбрасываем на первую страницу при изменении фильтра
    } else {
      setStatus('');
      setCurrentPage(1);
    }
  };

  const handleUserClick = async (id: string) => {
    // Если мы на вкладке newTO, автоматически обновляем статус на inProgress
    if (activeTab === 'newTO' && id) {
      try {
        await TechnicalService.updateTechListStatus(id, 'inProgress', user.id);
        // После обновления статуса перезагружаем данные newTO
        fetchNewTOData();
      } catch (error) {
        if (isCheckStatusFinal(error)) {
          alert(CHECK_STATUS_FINAL_MESSAGE);
          fetchNewTOData();
        } else {
          console.error('Ошибка при обновлении статуса заявки:', error);
        }
        // Продолжаем открывать карточку даже если не удалось обновить статус
      }
    }

    setSelectedUserId(id);
  };

  const handlePageChange = (page: number) => {
    if (activeTab === 'closedTO') {
      setCurrentPage(page);
    } else if (activeTab === 'newTO') {
      setNewTOFilters((prev) => ({ ...prev, page }));
    }
  };

  const filterButtonDate = [
    {
      buttonLabel: 'Заключение',
      currentSelection: status,
      handler: handleStatusChange,
      style: { width: '210px' },
      labelData: [
        { i_id: '1', i_name: 'role', i_type: 'checkbox', i_value: 'completed', i_label: 'Допущен' },
        { i_id: '2', i_name: 'role', i_type: 'checkbox', i_value: 'rejected', i_label: 'Не допущен' },
        { i_id: '3', i_name: 'role', i_type: 'checkbox', i_value: 'waiting', i_label: 'Ожидает' },
      ],
    },
  ];

  return (
    <>
      <Header filter={filter} setFilter={setFilter} params={params} />
      {selectedUserId ? (
        <TechDetails id={selectedUserId} onUserClick={handleUserClick} />
      ) : (
        <div className="admin__container">
          <header className="admin__header">
            <div className="admin__header-container">
              <div className="tech-tabs">
                <div
                  className={`tech-tab ${activeTab === 'newTO' ? 'active' : ''}`}
                  onClick={() => setActiveTab('newTO')}
                >
                  <h1>Новые заявки на ТО</h1>
                </div>
                <div
                  className={`tech-tab ${activeTab === 'closedTO' ? 'active' : ''}`}
                  onClick={() => setActiveTab('closedTO')}
                >
                  <h1>
                    Завершенные заявки на ТО{' '}
                    {/* {(reduxTotalCount || totalCount) > 0 && `(${reduxTotalCount || totalCount})`} */}
                  </h1>
                </div>
                <div
                  className={`tech-tab ${activeTab === 'auto' ? 'active' : ''}`}
                  onClick={() => setActiveTab('auto')}
                >
                  <h1>Автосогласование</h1>
                </div>
              </div>
              {(activeTab === 'closedTO' || activeTab === 'newTO') && (
                <StatusButtons filterButtonDate={filterButtonDate} />
              )}
            </div>
          </header>

          {activeTab === 'newTO' ? (
            <TableContainer>
              {/* Блок статистики техосмотров для newTO */}
              <div className="techStatsText" style={{ fontWeight: 'bold', color: '#333' }}>
                Осмотров: {newTOStats.totalCount}, из них Допущены {newTOStats.completeCount}, Не допущены{' '}
                {newTOStats.uncompleteCount}, Ожидают {newTOStats.waitingCount}
              </div>
              {/* Блок фильтров для newTO */}
              <div className="techFiltersContainer" style={{ padding: '20px 0px', borderBottom: 'none' }}>
                <div className="techFiltersRow">
                  <div className="techFilterItem">
                    <label>Начало периода</label>
                    <input
                      type="date"
                      name="startTime"
                      value={newTOFilters.startTime}
                      onChange={handleNewTOFilterChange}
                      placeholder="DD-MM-YYYY"
                    />
                  </div>
                  <div className="techFilterItem">
                    <label>Конец периода</label>
                    <input
                      type="date"
                      name="endTime"
                      value={newTOFilters.endTime}
                      onChange={handleNewTOFilterChange}
                      placeholder="DD-MM-YYYY"
                    />
                  </div>
                  <div className="techFilterItem">
                    <label>Номер заявки</label>
                    <input
                      type="text"
                      name="num"
                      value={newTOFilters.num}
                      onChange={handleNewTOFilterChange}
                      placeholder="Номер заявки"
                    />
                  </div>
                  <div className="techFilterItem">
                    <label>Гос. номер</label>
                    <input
                      type="text"
                      name="number"
                      value={newTOFilters.number}
                      onChange={handleNewTOFilterChange}
                      placeholder="Гос. номер"
                    />
                  </div>
                  <div className="techFilterItem">
                    <button className="resetFiltersBtn" onClick={resetNewTOFilters}>
                      Сбросить фильтры
                    </button>
                  </div>
                </div>
              </div>

              {newTOLoading ? (
                <div className="loading__indicator">Загрузка...</div>
              ) : newTOError ? (
                <div className="error__message">{newTOError}</div>
              ) : (
                <>
                  <TechList sortedAndSearchedPosts={newTOData} onUserClick={handleUserClick} />

                  {/* Компонент пагинации для newTO */}
                  <div className="pagination-wrapper">
                    <Pagination
                      currentPage={newTOPagination.currentPage}
                      totalPages={newTOPagination.totalPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </>
              )}
            </TableContainer>
          ) : activeTab === 'closedTO' ? (
            <TableContainer>
              {/* Блок статистики техосмотров */}
              <div className="techStatsText" style={{ fontWeight: 'bold', color: '#333' }}>
                Осмотров: {techStats.totalCount}, из них Допущены {techStats.completeCount}, Не допущены{' '}
                {techStats.uncompleteCount}, Ожидают {techStats.waitingCount}
              </div>
              {/* Блок фильтров */}
              <div className="techFiltersContainer" style={{ padding: '20px 0px', borderBottom: 'none' }}>
                <div className="techFiltersRow">
                  <div className="techFilterItem">
                    <label>Начало периода</label>
                    <input
                      type="date"
                      name="startTime"
                      value={extendedFilters.startTime}
                      onChange={handleExtendedFilterChange}
                      placeholder="DD-MM-YYYY"
                    />
                  </div>
                  <div className="techFilterItem">
                    <label>Конец периода</label>
                    <input
                      type="date"
                      name="endTime"
                      value={extendedFilters.endTime}
                      onChange={handleExtendedFilterChange}
                      placeholder="DD-MM-YYYY"
                    />
                  </div>
                  <div className="techFilterItem">
                    <label>Автопарк</label>
                    <input
                      type="text"
                      name="autoparkName"
                      value={extendedFilters.autoparkName}
                      onChange={handleExtendedFilterChange}
                      placeholder="Название автопарка"
                    />
                  </div>
                  <div className="techFilterItem">
                    <label>Номер заявки</label>
                    <input
                      type="text"
                      name="num"
                      value={extendedFilters.num}
                      onChange={handleExtendedFilterChange}
                      placeholder="Номер заявки"
                    />
                  </div>
                  <div className="techFilterItem">
                    <label>Гос. номер</label>
                    <input
                      type="text"
                      name="carNumber"
                      value={extendedFilters.carNumber}
                      onChange={handleExtendedFilterChange}
                      placeholder="Гос. номер"
                    />
                  </div>
                  <div className="techFilterItem">
                    <button className="resetFiltersBtn" onClick={resetFilters}>
                      Сбросить фильтры
                    </button>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="loading__indicator">Загрузка...</div>
              ) : error ? (
                <div className="error__message">{error}</div>
              ) : (
                <>
                  <TechList
                    sortedAndSearchedPosts={(reduxTechLists as any[]) || (techLists as any[])}
                    onUserClick={handleUserClick}
                  />

                  {/* Компонент пагинации */}
                  <div className="pagination-wrapper">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={reduxTotalPages || totalPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </>
              )}
            </TableContainer>
          ) : (
            <TableContainer>
              <AutoApproval />
            </TableContainer>
          )}
        </div>
      )}
    </>
  );
}

export default Tech;
